import { copyToClipboard, showToast } from 'BreetHelpers';
import type { MouseEvent, ReactNode } from 'react';

type NestedKeys<T> = {
	[K in keyof T & string]: T[K] extends (...args: unknown[]) => unknown
		? `${K}`
		: T[K] extends readonly unknown[]
			? `${K}`
			: T[K] extends object
				? `${K}` | `${K}.${NestedKeys<T[K]>}`
				: `${K}`;
}[keyof T & string];

export interface ITableColumn<E> {
	key: NestedKeys<E> | 'extra';
	searchKey?: string;
	dataIndex?: keyof E;
	title?: string | ReactNode;
	width?: number;
	render?: (props: { row: E; column: ITableColumn<E> }) => ReactNode;
	onClick?: (props: { row: E; column: ITableColumn<E> }) => Promise<void> | void;
	fixed?: boolean;
	hideColumn?: boolean;
	copy?: boolean;
	copyMessage?: string;
	onSearch?: (data: Record<string, string>) => void;
}

export interface ITable<E> {
	data?: E[];
	pageLimit?: number;
	isLoading: boolean;
	columns: ITableColumn<E>[];
	onRowClick?: (rowItem: E) => void;
	hideHeader?: boolean;
	isSearchFilter?: boolean;
	skeletonRows?: number;
}

export const getValueByPath = (obj: Record<string, unknown>, path: string): unknown =>
	path.split('.').reduce((acc: unknown, key) => {
		if (acc && typeof acc === 'object' && key in acc) {
			return (acc as Record<string, unknown>)[key];
		}
		return undefined;
	}, obj);

export const getRowKey = (
	item:
		| {
				_id?: string;
		  }
		| {
				id?: string;
		  }
) => {
	if ('_id' in item) return item._id;
	if ('id' in item) return item.id;
	return '';
};

export const createTableColumn = <E>(column: ITableColumn<E>): ITableColumn<E> => column;

export interface ITablePlainColumn<E> {
	title?: string;
	value?: string;
	width?: number;
	render?: (props: { row: E }) => ReactNode;
	onClick?: (props: { row: E }) => Promise<void> | void;
	fixed?: boolean;
	hideColumn?: boolean;
	className?: string;
}

export interface ITablePlainProps<E> {
	hideHeader?: boolean;
	columns: ITablePlainColumn<E>[];
	data: E[];
	rowClass?: string;
	isLoading?: boolean;
	skeletonRows: number;
}

export const mapTableWithId = <E>(item: E, index: number) => ({ ...item, id: String(index) });

export const handleTableCellCopy = (text: string, message: string) => (e: MouseEvent<SVGSVGElement>) => {
	e.stopPropagation();
	copyToClipboard(text, message)().catch(() => {
		showToast('error', 'Failed to copy');
	});
};

export interface ITableBodyLoaderProps {
	skeletonRows: number;
	skeletonColumns: number;
}
